import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { disablePublicShare, enablePublicShare } from '@/lib/publicShare';
import { pb } from '@/lib/pocketbase';
import { useSimulatorState } from '@/store/useSimulatorState';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export function ShareDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const simulationId = useSimulatorState((s) => s.simulationId);
  const [publicEnabled, setPublicEnabled] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !simulationId) return;
    (async () => {
      try {
        const sim = await pb
          .collection('simulations')
          .getOne<{ publicEnabled: boolean; publicSlug: string | null }>(simulationId);
        setPublicEnabled(sim.publicEnabled);
        setShareUrl(sim.publicSlug ? `${window.location.origin}/p/${sim.publicSlug}` : null);
      } catch {
        toast.error('Impossible de charger le statut de partage.');
      }
    })();
  }, [open, simulationId]);

  const handleEnable = async () => {
    if (!simulationId) return;
    setIsLoading(true);
    try {
      const url = await enablePublicShare(simulationId);
      setShareUrl(url);
      setPublicEnabled(true);
      await navigator.clipboard.writeText(url);
      toast.success('Lien public activé et copié dans le presse-papier.');
    } catch {
      toast.error('Impossible d’activer le partage public.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!simulationId) return;
    setIsLoading(true);
    try {
      await disablePublicShare(simulationId);
      setPublicEnabled(false);
      toast.success('Lien public désactivé.');
    } catch {
      toast.error('Impossible de désactiver le partage public.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié dans le presse-papier !');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lien public en lecture seule</DialogTitle>
          <DialogDescription>
            Toute personne disposant du lien pourra consulter cette simulation, sans compte et sans
            pouvoir la modifier.
          </DialogDescription>
        </DialogHeader>

        {publicEnabled && shareUrl ? (
          <div className="flex gap-2">
            <Input readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
            <Button variant="outline" onClick={handleCopy}>
              Copier
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Le partage public est actuellement désactivé.
          </p>
        )}

        <DialogFooter>
          {publicEnabled ? (
            <Button variant="destructive" onClick={handleDisable} disabled={isLoading}>
              Désactiver le lien
            </Button>
          ) : (
            <Button onClick={handleEnable} disabled={isLoading}>
              Activer le lien public
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
